import React from 'react';
import { X } from 'lucide-react';

// ONE MODAL LOOK (owner, 16 Oct 2026). The reference is the tenant admin's
// Doctors → Add Doctor modal (components/modals/AddDoctor.jsx): a white
// rounded card on a dimmed page, the title in teal on a light teal strip, a
// grey rounded close button, a scrolling body, and a footer with a grey Cancel
// and a teal primary on the right. Every modal is drawn through this frame so
// they cannot drift apart again; the Pro-App draws the same header and footer
// (ui.jsx ModalHeader / ModalActions).
//
//   <ModalShell title="Add user" onClose={close} size="3xl"
//       footer={<ModalFooter onCancel={close} onSubmit={save} submitLabel="Create" busy={saving} />}>
//     …fields…
//   </ModalShell>

const SIZES = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
};

export const MODAL_CANCEL_CLASS = 'px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100';
export const MODAL_SUBMIT_CLASS = 'px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100';
export const MODAL_DANGER_CLASS = 'px-4 py-2 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100';

/** The footer buttons: grey Cancel, teal primary (red with `danger`). */
export function ModalFooter({
    onCancel, cancelLabel = 'Cancel', onSubmit, submitLabel = 'Save', submitType = 'button', form,
    busy = false, disabled = false, danger = false, cancelTestId = 'cancel-button', submitTestId = 'submit-button',
    children,
}) {
    return (
        <>
            {children}
            {onCancel && (
                <button type="button" onClick={onCancel} disabled={busy} data-testid={cancelTestId} className={MODAL_CANCEL_CLASS}>
                    {cancelLabel}
                </button>
            )}
            {(onSubmit || submitType === 'submit') && (
                <button
                    type={submitType}
                    form={form}
                    onClick={onSubmit}
                    disabled={busy || disabled}
                    data-testid={submitTestId}
                    className={danger ? MODAL_DANGER_CLASS : MODAL_SUBMIT_CLASS}
                >
                    {busy && <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin" />}
                    {submitLabel}
                </button>
            )}
        </>
    );
}

export default function ModalShell({
    title,
    onClose,
    children,
    footer,
    size = '3xl',
    closeDisabled = false,
    closeLabel = 'Close',
    testId,
    closeTestId,
    contentTestId,
    bodyClassName = '',
    zClass = 'z-50',
}) {
    return (
        <div
            data-testid={testId}
            role="dialog"
            aria-modal="true"
            aria-label={typeof title === 'string' ? title : undefined}
            className={`fixed inset-0 bg-black/50 flex items-center justify-center ${zClass} p-2 sm:p-4`}
        >
            <div data-testid={contentTestId} className={`bg-white my-5 rounded-2xl shadow-2xl w-full ${SIZES[size] || size} max-h-[80vh] sm:max-h-[90vh] flex flex-col`}>
                <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center gap-3 border-b border-gray-200 rounded-t-2xl">
                    <h3 className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent min-w-0">
                        {title}
                    </h3>
                    {onClose && (
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={closeDisabled}
                            aria-label={closeLabel}
                            data-testid={closeTestId || (testId ? `${testId}-close` : 'modal-close-button')}
                            className="text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <X className="h-5 w-5 sm:h-6 sm:w-6" />
                        </button>
                    )}
                </div>
                <div className={`flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar ${bodyClassName}`}>
                    {children}
                </div>
                {footer && (
                    <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-b-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4 flex-wrap">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}
